import React, { useState } from 'react';
import { API_BASE_URL } from './config';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'General Enquiry',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/inquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit enquiry');
      }
      
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', phone: '', subject: 'General Enquiry', message: '' });
      }, 3000);
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      alert('Failed to send inquiry. Please check if backend is running or try again later.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="bg-slate-50 min-h-screen font-sans pb-20">
      
      {/* ── 1. HERO BANNER ── */}
      <div className="relative text-white overflow-hidden" style={{ minHeight: '340px' }}>
        <img src="/SG (10).jpg" alt="Contact Us" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529] via-[#0d2d6b]/90 to-[#0d2d6b]/60" />
        <div className="absolute top-0 right-0 opacity-5 text-[180px] font-black select-none leading-none -mr-8 -mt-8">SGCL</div>
        
        <div className="relative z-10 px-4 md:px-20 py-16 max-w-4xl">
          <span className="bg-govt-orange text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">
            Official Helpdesk
          </span>
          <h1 className="text-4xl md:text-6xl font-black mt-4 mb-5 leading-tight uppercase italic">
            Contact &amp; <span className="text-govt-orange">Support</span>
          </h1>
          <p className="text-blue-100 font-bold border-l-4 border-govt-orange pl-5 italic max-w-2xl leading-relaxed text-base">
            Get in touch with Shramjivi Goodshed Corporation Limited (SGCL). For registration enquiries, grievance registration, and corporate partnerships.
          </p>
        </div>

        {/* Tricolor border strip */}
        <div className="absolute bottom-0 left-0 right-0 h-1.5 flex">
          <div className="flex-1 bg-[#FF9933]" />
          <div className="flex-1 bg-white" />
          <div className="flex-1 bg-[#138808]" />
        </div>
      </div>

      {/* ── 2. MAIN GRID SECTION ── */}
      <div className="container mx-auto px-4 md:px-20 mt-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          
          {/* Left Side: Contact Form */}
          <div className="lg:col-span-7 bg-white rounded-3xl shadow-xl border border-slate-100 p-8 md:p-10 relative overflow-hidden">
            {/* saffron accent border */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#FF9933]" />
            
            <div className="mb-6">
              <span className="text-govt-orange text-[10px] font-black uppercase tracking-widest block mb-1">Grievance &amp; Queries</span>
              <h3 className="font-black text-govt-blue text-xl uppercase tracking-wider">Send a Message</h3>
              <p className="text-slate-400 text-xs font-semibold mt-1">Submit your request below, and our response officer will contact you within 24 hours.</p>
            </div>

            {submitted ? (
              <div className="bg-green-50 border border-green-200 text-green-800 rounded-2xl p-6 text-center animate-in fade-in zoom-in-95 duration-300">
                <span className="text-4xl block mb-3">✅</span>
                <h4 className="font-black uppercase text-sm mb-1 text-green-900">Message Submitted</h4>
                <p className="text-xs font-bold text-green-700/90 leading-relaxed">Your support ticket has been created. A verification email has been sent to your registered address.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1.5">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your name"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:border-govt-blue focus:ring-2 focus:ring-govt-blue/10 transition-all font-semibold text-slate-800 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 XXXXX XXXXX"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:border-govt-blue focus:ring-2 focus:ring-govt-blue/10 transition-all font-semibold text-slate-800 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1.5">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="name@example.com"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:border-govt-blue focus:ring-2 focus:ring-govt-blue/10 transition-all font-semibold text-slate-800 text-sm"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1.5">Subject Category</label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:border-govt-blue focus:ring-2 focus:ring-govt-blue/10 transition-all font-bold text-slate-800 text-sm bg-white"
                  >
                    <option value="General Enquiry">General Enquiry</option>
                    <option value="Worker Registration">Worker Registration &amp; Enrolment</option>
                    <option value="Grievance Redressal">Grievance / Complaint Desk</option>
                    <option value="Freight Services">Freight &amp; Partner ID Enquiries</option>
                    <option value="Compliance &amp; Legal">Compliance &amp; Statutory Matters</option>
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1.5">Message / Details</label>
                  <textarea
                    name="message"
                    required
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Provide full description of your query or grievance..."
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:border-govt-blue focus:ring-2 focus:ring-govt-blue/10 transition-all font-semibold text-slate-800 text-sm"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full py-4 bg-govt-orange text-white rounded-xl font-black uppercase text-xs tracking-wider shadow-[0_5px_0_rgb(194,65,12)] hover:brightness-110 active:translate-y-1 transition-all"
                >
                  Submit Support Ticket
                </button>
              </form>
            )}
          </div>

          {/* Right Side: Office Addresses & Contacts */}
          <div className="lg:col-span-5 space-y-6">
            
            {/* Quick Contacts */}
            <div className="bg-white rounded-3xl p-6 shadow-xl border border-slate-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#138808]" />
              <h4 className="text-sm font-black text-govt-blue uppercase tracking-widest mb-4 border-b border-slate-100 pb-2">Direct Contact Helpdesk</h4>
              <div className="space-y-4">
                <div className="flex gap-4 items-start">
                  <span className="text-xl">✉️</span>
                  <div>
                    <p className="text-[9px] font-black uppercase text-slate-400">Official Support Email</p>
                    <a href="mailto:support@shramjivicorp.com" className="text-sm font-black text-[#FF9933] hover:underline block">
                      support@shramjivicorp.com
                    </a>
                  </div>
                </div>
                <div className="flex gap-4 items-start border-t border-slate-50 pt-3">
                  <span className="text-xl">✉️</span>
                  <div>
                    <p className="text-[9px] font-black uppercase text-slate-400">NFR Zone Support Email</p>
                    <a href="mailto:shramjivinortheast@gmail.com" className="text-sm font-black text-[#FF9933] hover:underline block">
                      shramjivinortheast@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex gap-4 items-start border-t border-slate-50 pt-3">
                  <span className="text-xl">🕰️</span>
                  <div>
                    <p className="text-[9px] font-black uppercase text-slate-400">Operational Hours</p>
                    <p className="text-xs font-bold text-slate-500">
                      Monday to Saturday | 09:30 AM – 06:00 PM <br />
                      <span className="text-[9px] text-slate-400 font-semibold">(Operational emergency lines are 24/7 active)</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Registered & Head Offices */}
            <div className="bg-white rounded-3xl p-6 shadow-xl border border-slate-100 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-govt-blue" />
              <h4 className="text-sm font-black text-govt-blue uppercase tracking-widest mb-4 border-b border-slate-100 pb-2">Registered &amp; Head Offices</h4>
              <div className="space-y-4">
                <div>
                  <p className="text-[9px] font-black uppercase text-govt-orange">Registered Office</p>
                  <p className="text-xs font-bold text-slate-800 uppercase mt-0.5 leading-snug">
                    D/14, Satyajit Roy Co-operative Market, Champasari More, Ward No. 46, Pradhan Nagar, Siliguri, Darjeeling, West Bengal – 734003
                  </p>
                </div>
                <div className="border-t border-slate-50 pt-3">
                  <p className="text-[9px] font-black uppercase text-govt-orange">Zonal Office – NFR Zone</p>
                  <p className="text-xs font-bold text-slate-800 uppercase mt-0.5 leading-snug">
                    15, Bhaskar Nagar School Path, R.G. Baruah Road, Bamunimaidan, Kamrup (Metro), Assam – 781021
                  </p>
                </div>
                <div className="border-t border-slate-50 pt-3">
                  <p className="text-[9px] font-black uppercase text-govt-orange">Head Office</p>
                  <p className="text-xs font-bold text-slate-800 uppercase mt-0.5 leading-snug">
                    D7, 1st Floor, Ranjit Nagar Commercial Complex, Behind Satyam Cinema, Near Shadipur Metro Station, Gate No. 5, New Delhi – 110008
                  </p>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>

      {/* ── 3. FULL SCREEN WIDTH GOOGLE MAP ── */}
      <div className="mt-16 w-full h-[450px] relative border-t border-b border-slate-200 shadow-inner overflow-hidden">
        <iframe
          title="Office Location"
          src="https://maps.google.com/maps?q=D/14%2C%20Satyajit%20Roy%20Co-operative%20Market%2C%20Champasari%20More%2C%20Ward%20No.%2046%2C%20Pradhan%20Nagar%2C%20Siliguri%2C%20Darjeeling%2C%20West%20Bengal%20-%20734003&t=&z=16&ie=UTF8&iwloc=&output=embed"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          className="grayscale hover:grayscale-0 transition-all duration-500"
        ></iframe>
      </div>
      
    </div>
  );
};

export default Contact;